import { defineResource } from '@tnn/core'
import { z } from 'zod'

/**
 * Salary advances (เบิกเงินล่วงหน้า): asked for in Thananan OS, up to a share of the pay earned so
 * far in the current period; approved like leave (the person's manager, their branch's lead or
 * Payroll, never themselves); paid in cash from the payer's reserve or by bank transfer; taken back
 * by the payroll run whose period holds the day it was paid (an ADVANCE deduction).
 */

export const advanceStatuses = ['pending', 'approved', 'paid', 'rejected', 'cancelled'] as const
export type AdvanceStatus = (typeof advanceStatuses)[number]
/** `cash`: from the payer's reserve (เงินสดย่อย); `transfer`: to the person's bank account */
export const advanceMethods = ['cash', 'transfer'] as const

/**
 * payroll.advance: one request. `pay`: the payer hands over cash (their reserve goes down, a cash
 * entry) or records the transfer; the ADVANCE deduction is made then. A paid advance stays; one
 * pending or approved may be cancelled (the person: their own while pending).
 */
export const advance = defineResource({
  app: 'payroll',
  name: 'advance',
  path: 'advances',
  schema: z.object({
    employeeId: z.string().min(1),
    amount: z.number().positive().max(1_000_000),
    method: z.enum(advanceMethods).default('cash'),
    reason: z.string().trim().max(500).nullish(),
  }),
  fields: {
    /** ADV-6910-0001 (server-set) */
    number: { readOnly: true, search: true, list: { width: 140, priority: 1 } },
    employeeId: {
      type: 'ref',
      ref: { resource: 'hr.employee' },
      filter: true,
      search: true,
      list: { priority: 1 },
      form: { span: 6, custom: true },
    },
    amount: { type: 'money', list: { align: 'end', width: 130, priority: 1 }, form: { span: 6 } },
    method: { filter: true, list: { width: 120, priority: 2 }, form: { span: 6 } },
    reason: { type: 'textarea', list: { priority: 3 }, form: { span: 12 } },
    status: {
      readOnly: true,
      type: 'select',
      options: advanceStatuses,
      filter: true,
      tones: { pending: 'warning', approved: 'info', paid: 'success', rejected: 'danger', cancelled: 'neutral' },
      list: { width: 120, priority: 1 },
    },
    /** What could be asked for when it was asked (server-set): the limit at that moment */
    available: { readOnly: true, type: 'money', list: false },
    userId: { readOnly: true, type: 'ref', ref: { resource: 'admin.user' }, list: false },
    branchId: { readOnly: true, type: 'ref', ref: { resource: 'company.branch' }, filter: true, list: { priority: 2 } },
    requestedByUserId: { readOnly: true, type: 'ref', ref: { resource: 'admin.user' }, list: { priority: 3, hidden: true } },
    requestedAt: { readOnly: true, type: 'datetime', filter: true, list: { width: 160, priority: 2 } },
    reviewedByUserId: { readOnly: true, type: 'ref', ref: { resource: 'admin.user' }, list: { priority: 3, hidden: true } },
    reviewedAt: { readOnly: true, type: 'datetime', list: { priority: 3, hidden: true } },
    rejectReason: { readOnly: true, list: false },
    paidByUserId: { readOnly: true, type: 'ref', ref: { resource: 'admin.user' }, list: { priority: 3, hidden: true } },
    paidAt: { readOnly: true, type: 'datetime', list: { priority: 3, hidden: true } },
    /** How it was paid in the end, and the transfer's reference */
    paidMethod: { readOnly: true, type: 'select', options: advanceMethods, optionLabels: 'payroll:advance.options.method', list: false },
    paidReference: { readOnly: true, list: false },
    /** The ADVANCE deduction it made (payroll.payAdjustment) */
    adjustmentId: { readOnly: true, type: 'ref', ref: { resource: 'payroll.payAdjustment' }, list: false },
  },
  title: 'number',
  columns: ['number', 'requestedAt', 'employeeId', 'amount', 'method', 'status', 'branchId', 'reason', 'reviewedByUserId', 'paidByUserId'],
  defaultSort: '-requestedAt',
  // recordFor: a lead asks for someone without a phone
  actions: ['read', 'create', 'export', 'print', 'approve', 'cancel', 'pay', 'recordFor'],
  ownerField: ['userId', 'requestedByUserId'],
  branchFields: ['branchId'],
  card: { title: 'employeeId', subtitle: 'number', meta: ['amount', 'requestedAt'], status: 'status' },
})

/** Advances that count against the limit: asked, approved or paid (not rejected or cancelled) */
export const advanceCounted: readonly AdvanceStatus[] = ['pending', 'approved', 'paid']

/**
 * What may still be asked for in the period: the share of the pay earned so far (whole baht,
 * down), less the advances already counted in it
 */
export function advanceLimit(earned: number, percent: number, taken: number): number {
  return Math.max(0, Math.floor((Math.max(0, earned) * percent) / 100) - Math.max(0, taken))
}
